import { callZohoAPI } from '../utils/api'

export interface CrmContact {
  id: string
  First_Name: string
  Last_Name: string
  Full_Name?: string
  Email: string
  Phone?: string
  Mobile?: string
  Title?: string
  Department?: string
  Tags?: string
  Mailing_List?: string
  Categories?: string
  Region?: string
  Sub_Region?: string
  Team?: string
  Status?: string
  Form_Name?: string
  Bucket_ID?: string
  Created_Time?: string
  Modified_Time?: string
  Owner?: {
    name: string
    id: string
    email: string
  }
}

export interface CrmListResponse {
  data: CrmContact[]
  info: {
    per_page: number
    count: number
    page: number
    more_records: boolean
  }
}

/**
 * Normalize multiselect lookup fields from array format to comma-separated string.
 * Zoho CRM may return these as:
 *   - An array of objects: [{name: "Test 2", id: "123"}, ...]
 *   - A comma-separated string: "Test 2, Test 1"
 *   - null/undefined
 */
function normalizeContact(contact: any): CrmContact {
  const normalized = { ...contact }

  for (const field of ['Mailing_List', 'Categories', 'Tags']) {
    const value = normalized[field]
    if (Array.isArray(value)) {
      normalized[field] = value
        .map((item: any) => {
          if (typeof item === 'string') return item
          return item?.name || item?.Name || item?.display_value || ''
        })
        .filter(Boolean)
        .join(', ')
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      normalized[field] = value.name || value.Name || ''
    }
  }

  return normalized as CrmContact
}

export const contactsApi = {
  // Fetch list of contacts — no fields restriction so multiselect lookups are included
  list: async (page = 1, perPage = 50): Promise<CrmListResponse> => {
    const response = await callZohoAPI(
      `crm/v8/Contacts?page=${page}&per_page=${perPage}`,
    )
    if (!response) {
      return {
        data: [],
        info: { per_page: perPage, count: 0, page: 1, more_records: false },
      }
    }
    // Normalize multiselect lookup fields
    const rawData = response.data || []
    const normalizedData = rawData.map(normalizeContact)
    return {
      data: normalizedData,
      info: response.info || {
        per_page: perPage,
        count: 0,
        page,
        more_records: false,
      },
    }
  },

  // Get single contact details
  get: async (id: string): Promise<CrmContact | null> => {
    const response = await callZohoAPI(`crm/v8/Contacts/${id}`)
    if (response?.data?.[0]) {
      return normalizeContact(response.data[0])
    }
    return null
  },

  // Create new contact
  create: async (contact: Partial<CrmContact>): Promise<any> => {
    const payload = {
      data: [contact],
    }
    return await callZohoAPI('crm/v8/Contacts', 'POST', payload)
  },

  // Update existing contact
  update: async (id: string, contact: Partial<CrmContact>): Promise<any> => {
    const payload = {
      data: [
        {
          id,
          ...contact,
        },
      ],
    }
    return await callZohoAPI('crm/v8/Contacts', 'PUT', payload)
  },

  // Delete contact
  delete: async (id: string): Promise<any> => {
    return await callZohoAPI(`crm/v8/Contacts?ids=${id}`, 'DELETE')
  },

  /**
   * Search for a contact by email address.
   * Uses Zoho CRM Search API: GET /crm/v8/Contacts/search?criteria=(Email:equals:...)
   * Returns the first match, or null if not found.
   */
  findByEmail: async (email: string): Promise<CrmContact | null> => {
    try {
      const encoded = encodeURIComponent(`(Email:equals:${email})`)
      const response = await callZohoAPI(
        `crm/v8/Contacts/search?criteria=${encoded}`,
      )
      if (response?.data?.[0]) {
        return normalizeContact(response.data[0])
      }
      return null
    } catch (err) {
      // No records found = null (not an error)
      const msg = err instanceof Error ? err.message : String(err)
      if (
        msg.includes('NO_CONTENT') ||
        msg.includes('204') ||
        msg.includes('no data')
      ) {
        return null
      }
      throw err
    }
  },

  /**
   * Upsert a contact by email — the correct pattern for syncing form submissions to CRM.
   *
   * Logic:
   *   1. Search for existing contact by email
   *   2. FOUND → merge mailing lists / tags (append, don't replace) then UPDATE
   *   3. NOT FOUND → CREATE new contact
   *
   * This ensures:
   *   - No duplicate contacts in CRM (one record per person)
   *   - Multiple form submissions from the same email accumulate mailing lists & tags
   *   - Creator keeps all submission records as the full audit trail
   *
   * @param contactData  The new contact data from the form submission
   * @returns { action: 'created' | 'updated', contactId: string }
   */
  upsert: async (
    contactData: Partial<CrmContact>,
  ): Promise<{ action: 'created' | 'updated'; contactId: string }> => {
    if (!contactData.Email) {
      throw new Error('Email is required for contact upsert')
    }

    const existing = await contactsApi.findByEmail(contactData.Email)

    if (!existing) {
      // ── CREATE: no existing contact with this email ──
      const result = await contactsApi.create(contactData)
      const newId =
        result?.data?.[0]?.details?.id || result?.data?.[0]?.id || ''
      return { action: 'created', contactId: String(newId) }
    }

    // ── UPDATE: merge mailing lists, tags, and categories ──
    const merged: Partial<CrmContact> = { ...contactData }

    // Helper: merge comma-separated field values (append new, deduplicate)
    function mergeField(
      existingVal: string | undefined,
      newVal: string | undefined,
    ): string | undefined {
      if (!newVal) return existingVal
      if (!existingVal) return newVal
      const existingItems = existingVal
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const newItems = newVal
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const merged = [...existingItems]
      for (const item of newItems) {
        if (!merged.some((e) => e.toLowerCase() === item.toLowerCase())) {
          merged.push(item)
        }
      }
      return merged.join(', ')
    }

    // Append mailing lists (don't replace — contact may be on multiple lists)
    merged.Mailing_List = mergeField(
      existing.Mailing_List,
      contactData.Mailing_List,
    )

    // Append tags (don't replace)
    merged.Tags = mergeField(existing.Tags, contactData.Tags)

    // Append categories (don't replace)
    merged.Categories = mergeField(existing.Categories, contactData.Categories)

    // Don't overwrite these with empty values if they already exist
    if (!merged.First_Name && existing.First_Name)
      merged.First_Name = existing.First_Name
    if (!merged.Last_Name && existing.Last_Name)
      merged.Last_Name = existing.Last_Name
    if (!merged.Phone && existing.Phone) merged.Phone = existing.Phone
    if (!merged.Mobile && existing.Mobile) merged.Mobile = existing.Mobile

    await contactsApi.update(existing.id, merged)
    return { action: 'updated', contactId: existing.id }
  },
}
